// src/widgets/OnlineWidget.jsx
//
// Dua widget online dalam satu file:
//
//   <OnlineCountWidget filter={{ realm: 'myrealm' }} />
//   — Menampilkan jumlah user online sekarang (angka besar)
//
//   <OnlineByRealmWidget filter={{ limit: 10 }} />
//   — Menampilkan ranking realm berdasarkan jumlah user online sekarang

import useWidgetData from '../hooks/useWidgetData';
import { Wifi } from 'lucide-react';

// ── OnlineCountWidget ───────────────────────────────────────────────────────
export function OnlineCountWidget({ filter = {} }) {
  const { data, loading, error } = useWidgetData('online_count', filter);
  const count = data?.online_count ?? null;

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '20px 22px', boxSizing: 'border-box',
      overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Online Sekarang
        </div>
        <div style={{ padding: 8, background: 'rgba(34,197,94,0.12)', borderRadius: 8 }}>
          <Wifi size={16} color="var(--accent-green, #22c55e)" />
        </div>
      </div>

      <div style={{
        fontSize: 36, fontWeight: 700, fontFamily: 'var(--font-mono)',
        color: loading || error ? 'var(--text-muted)' : 'var(--accent-green, #22c55e)',
      }}>
        {loading ? '…' : error ? '!' : (count ?? 0).toLocaleString()}
      </div>

      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
        {error ? error : 'sesi aktif (stop time = epoch 0)'}
      </div>
    </div>
  );
}

// ── OnlineByRealmWidget ──────────────────────────────────────────────────────
export function OnlineByRealmWidget({ filter = {} }) {
  const { data, loading, error } = useWidgetData('online_by_realm', filter);
  const rows = Array.isArray(data) ? data : [];
  const total = rows.reduce((s, r) => s + Number(r.online_count || 0), 0);

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '16px 18px', boxSizing: 'border-box',
      overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Online per Realm
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--accent-green, #22c55e)' }}>
          {loading ? '…' : total.toLocaleString()}
        </div>
      </div>

      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 12 }}>Memuat…</div>
      ) : error ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-red)', fontSize: 12 }}>{error}</div>
      ) : rows.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 12 }}>Tidak ada user online</div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map((row, i) => {
            const pct = total > 0 ? (Number(row.online_count) / total) * 100 : 0;
            return (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    {/* pulsing dot for top realm */}
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                      background: i === 0 ? 'var(--accent-green, #22c55e)' : 'var(--border)',
                    }} />
                    <span style={{ fontSize: 12, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.realm || '(empty)'}
                    </span>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)', flexShrink: 0, marginLeft: 8 }}>
                    {Number(row.online_count).toLocaleString()}
                  </span>
                </div>
                <div style={{ height: 3, background: 'var(--bg-input)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, minWidth: 2, height: '100%', background: 'var(--accent-green, #22c55e)', borderRadius: 2 }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}